import isObject from 'lodash/isObject'
import isFunction from 'lodash/isFunction'
import defaultsDeep from 'lodash/defaultsDeep'
import each from 'lodash/each'
import { maxRetries } from 'constants/apiConfig'
import { hardFailStatuses, networkErrorStatuses } from 'constants/status'

type ActionPayloadFactory = (...args: unknown[]) => Record<string, unknown>
type ActionCreatorArgname = string | ActionPayloadFactory | Record<string, unknown>

type UnknownRecord = Record<string, unknown>

const getStatus = (action: unknown): number | undefined => {
  const root = action as UnknownRecord
  const error = root.error as UnknownRecord | undefined
  const directStatus = error?.status
  if (typeof directStatus === 'number') {
    return directStatus
  }
  const response = error?.response as UnknownRecord | undefined
  const responseStatus = response?.status
  return typeof responseStatus === 'number' ? responseStatus : undefined
}

const getRetries = (action: unknown): number | undefined => {
  const root = action as UnknownRecord
  const meta = root.meta as UnknownRecord | undefined
  const previousAction = meta?.previousAction as UnknownRecord | undefined
  const previousMeta = previousAction?.meta as UnknownRecord | undefined
  const retries = previousMeta?.retries
  return typeof retries === 'number' ? retries : undefined
}

export const actionCreator = (type: string, ...argnames: ActionCreatorArgname[]) => {
  return (...args: unknown[]) => {
    let action: Record<string, unknown> = { type }
    let ans: ActionCreatorArgname[] = argnames

    if (isFunction(ans[0])) {
      action = defaultsDeep((ans[0] as ActionPayloadFactory)(...args), action)
      ans = ans.slice(1)
    } else if (isObject(ans[0])) {
      action = defaultsDeep(ans[0] as Record<string, unknown>, action)
      ans = ans.slice(1)
    }

    each(ans, (an: string, i: number) => {
      action[an] = args[i]
    })

    return action
  }
}

export const typesArray = (types: { REQUEST: string; RECEIVE: string; FAIL: string }) => {
  return [types.REQUEST, types.RECEIVE, types.FAIL]
}

export const isNetworkError = (action: unknown) => {
  const status = getStatus(action)
  return typeof status === 'number' ? networkErrorStatuses.includes(status) : false
}

export const isHardFail = (action: unknown) => {
  const root = action as UnknownRecord
  if (root.error == null) {
    return false
  }

  const status = getStatus(action)
  const retries = getRetries(action)

  if (retries == null || retries >= maxRetries) {
    return true
  }

  return typeof status === 'number' ? hardFailStatuses.includes(status) : false
}
